import React, { useContext, useEffect, useState, useMemo } from 'react';
import { Table, Button, Image, Badge, Checkbox, Card, Row, Col, Space, Collapse, Input, InputNumber, DatePicker, Modal, Form, Pagination } from 'antd';
import { FilterOutlined, SaveOutlined, CloseOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined, ExportOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useFetch } from '../../hooks';
import { LayoutContext } from '../../contexts';
import { URLS } from '../../constants';
import { getImageFallbackUrl, getMenuId } from '../../utils/helpers';
import { useSearchProducts, useCategories, useSubcategories, useBrands } from '../../hooks/useMenuQuery';

function Discounts() {
  const { setHeaderComponent, selectedLanguage, languages } = useContext(LayoutContext);
  const menuId = getMenuId();

  // API hook for updating discount
  const { doFetch: updateDiscountFetch } = useFetch();
  const { doFetch: bulkUpdateDiscountFetch } = useFetch();
  const { doFetch: removeDiscountFetch } = useFetch();

  // State declarations
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Filter states
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  // Table pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Create search parameters for products (including search query and filters)
  const searchParams = useMemo(() => {
    return {
      page: currentPage,
      per_page: pageSize,
      ...(debouncedSearchQuery.trim() && { q: debouncedSearchQuery.trim() }),
      ...(selectedCategories.length > 0 && { category_ids: selectedCategories }),
      ...(selectedSubcategories.length > 0 && { subcategory_ids: selectedSubcategories }),
      ...(selectedBrands.length > 0 && { brand_ids: selectedBrands }),
    };
  }, [currentPage, pageSize, debouncedSearchQuery, selectedCategories, selectedSubcategories, selectedBrands]);

  // Fetch categories, subcategories, brands and products
  const { data: categoriesData } = useCategories(menuId, 1, 100);
  const { data: subcategoriesData } = useSubcategories(menuId, 1, 100);
  const { data: brandsData } = useBrands(menuId, 1, 100);
  const { data: productsData, isLoading: isLoadingProducts, refetch: refetchProducts } = useSearchProducts(menuId, searchParams);

  // Selection states
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Edit states
  const [editingProduct, setEditingProduct] = useState(null);
  const [editedValues, setEditedValues] = useState({});

  // Bulk edit states
  const [bulkModalVisible, setBulkModalVisible] = useState(false);
  const [bulkForm] = Form.useForm();

  // Remove discount states
  const [removeModalVisible, setRemoveModalVisible] = useState(false);

  // Memoize filteredProducts to prevent recreation on every render
  const filteredProducts = useMemo(() => {
    if (!productsData?.products) {
      return [];
    }

    // Use selected language if available, otherwise fallback to first language
    const currentLanguage = selectedLanguage || languages?.[0]?.id;

    // Transform products to include name and nameTranslations fields
    return productsData.products.map(product => {
      // Find the primary name (use current language, fallback to any available translation)
      const primaryTranslation = product.product_translations?.find(
        translation => translation.language_id === currentLanguage
      ) || product.product_translations?.[0];

      const productName = primaryTranslation?.name_text || 'Unnamed Product';

      // Create nameTranslations HTML
      const nameTranslationsHtml = product.product_translations?.reduce((acc, translation) => {
        const languageName = languages?.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`;
        acc += `${languageName}: ${translation.name_text}<br />`;
        return acc;
      }, '') || 'No translations available';

      return {
        ...product,
        name: productName,
        nameTranslations: nameTranslationsHtml,
      };
    });
  }, [productsData?.products, languages, selectedLanguage]);

  // Memoize transformed categories for filters
  const transformedCategories = useMemo(() => {
    if (!categoriesData?.categories || !languages) return [];
    return categoriesData.categories.map(cat => ({
      id: cat.id,
      translations: cat.category_translations.map(translation => ({
        lanId: translation.language_id,
        lanName: languages.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`,
        name: translation.name_text,
      })),
    }));
  }, [categoriesData?.categories, languages]);

  // Memoize transformed subcategories for filters
  const transformedSubcategories = useMemo(() => {
    if (!subcategoriesData?.subcategories || !languages) return [];
    return subcategoriesData.subcategories.map(sub => ({
      id: sub.id,
      translations: sub.subcategory_translations.map(translation => ({
        lanId: translation.language_id,
        lanName: languages.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`,
        name: translation.name_text,
      })),
    }));
  }, [subcategoriesData?.subcategories, languages]);

  // Memoize transformed brands for filters
  const transformedBrands = useMemo(() => {
    if (!brandsData?.brands || !languages) return [];
    return brandsData.brands.map(brand => ({
      id: brand.id,
      translations: brand.brand_translations.map(translation => ({
        lanId: translation.language_id,
        lanName: languages.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`,
        name: translation.name_text,
      })),
    }));
  }, [brandsData?.brands, languages]);


  // Calculate total active filters
  const totalActiveFilters = useMemo(() => {
    return selectedCategories.length + selectedSubcategories.length + selectedBrands.length + (debouncedSearchQuery.trim() ? 1 : 0);
  }, [selectedCategories.length, selectedSubcategories.length, selectedBrands.length, debouncedSearchQuery]);

  useEffect(() => {
    setHeaderComponent(<b>{`Discounts`}</b>);

    return () => {
      setHeaderComponent(null);
    };
  }, [setHeaderComponent]);


  // Filter handlers
  const handleCategoryFilter = (categoryId, checked) => {
    if (checked) {
      setSelectedCategories(prev => [...prev, categoryId]);
    } else {
      setSelectedCategories(prev => prev.filter(id => id !== categoryId));
    }
  };

  const handleSubcategoryFilter = (subcategoryId, checked) => {
    if (checked) {
      setSelectedSubcategories(prev => [...prev, subcategoryId]);
    } else {
      setSelectedSubcategories(prev => prev.filter(id => id !== subcategoryId));
    }
  };

  const handleBrandFilter = (brandId, checked) => {
    if (checked) {
      setSelectedBrands(prev => [...prev, brandId]);
    } else {
      setSelectedBrands(prev => prev.filter(id => id !== brandId));
    }
  };

  const clearAllFilters = () => {
    setSelectedCategories([]);
    setSelectedSubcategories([]);
    setSelectedBrands([]);
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  const clearAllFiltersAndSearch = () => {
    clearAllFilters();
    clearSearch();
    setCurrentPage(1); // Reset to first page when clearing all
  };

  // Selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      // Select all products on current page
      setSelectedProducts(filteredProducts.map(product => product.id));
    } else {
      setSelectedProducts([]);
    }
  };

  const handleProductSelection = (productId, checked) => {
    if (checked) {
      setSelectedProducts(prev => [...prev, productId]);
    } else {
      setSelectedProducts(prev => prev.filter(id => id !== productId));
    }
  };

  // Update selectAll state when selectedProducts changes
  useEffect(() => {
    if (filteredProducts.length > 0) {
      const allFilteredIds = filteredProducts.map(product => product.id);
      const isAllSelected = allFilteredIds.every(id => selectedProducts.includes(id));
      setSelectAll(isAllSelected);
    } else {
      setSelectAll(false);
    }
  }, [selectedProducts, filteredProducts]);

  const clearSelection = () => {
    setSelectedProducts([]);
    setSelectAll(false);
  };

  // Helper functions for parsing types and sizes
  const parseTypes = (typesData) => {
    try {
      if (!typesData) return [];
      if (typeof typesData === 'string') {
        const parsed = JSON.parse(typesData);
        return Array.isArray(parsed) ? parsed : [];
      }
      return Array.isArray(typesData) ? typesData : [];
    } catch (error) {
      console.warn('Error parsing types data:', error);
      return [];
    }
  };

  const parseSizes = (sizesData) => {
    try {
      if (!sizesData) return [];
      if (typeof sizesData === 'string') {
        const parsed = JSON.parse(sizesData);
        return Array.isArray(parsed) ? parsed : [];
      }
      return Array.isArray(sizesData) ? sizesData : [];
    } catch (error) {
      console.warn('Error parsing sizes data:', error);
      return [];
    }
  };

  // Edit handlers
  const handleEditProduct = (product) => {
    const types = parseTypes(product.types);
    const sizes = parseSizes(product.sizes);

    const initialValues = {
      price: product.price,
      discount: product.discount,
      discount_valid_until: product.discount_valid_until,
      types: types.map(type => ({
        ...type,
        price: type.price,
      })),
      sizes: sizes.map(size => ({
        ...size,
        price: size.price,
        discount: size.discount,
      }))
    };

    setEditingProduct(product.id);
    setEditedValues(initialValues);
  };

  const handleDiscardEdit = () => {
    setEditingProduct(null);
    setEditedValues({});
  };

  const handleSaveEdit = async () => {
    console.log('Saving changes for product:', editingProduct, editedValues);

    // Prepare data for API call
    const updateData = {
      price: editedValues.price,
      discount: editedValues.discount,
      discount_valid_until: editedValues.discount_valid_until,
      types: editedValues.types,
      sizes: editedValues.sizes
    };

    updateDiscountFetch({
      url: URLS.updateProductDiscount({ productId: editingProduct }),
      method: 'PUT',
      params: updateData,
      onSuccess: (response) => {
        console.log('Discount update successful:', response);
        // Refresh products data to get updated values
        refetchProducts();
        // Exit edit mode
        setEditingProduct(null);
        setEditedValues({});
      },
      onError: (error) => {
        console.error('Failed to update discount:', error);
        // You could show an error notification here
      },
      successMessage: 'Product discount updated successfully!',
      showSuccessNotification: true
    });
  };

  const handleFieldChange = (field, value, typeIndex = null, sizeIndex = null) => {
    setEditedValues(prev => {
      const newValues = { ...prev };

      if (typeIndex !== null) {
        newValues.types[typeIndex][field] = value;
      } else if (sizeIndex !== null) {
        newValues.sizes[sizeIndex][field] = value;
      } else {
        newValues[field] = value;
      }

      return newValues;
    });
  };

  // Bulk edit handlers
  const handleBulkEdit = () => {
    setBulkModalVisible(true);
    bulkForm.resetFields();
  };

  const handleBulkCancel = () => {
    setBulkModalVisible(false);
    bulkForm.resetFields();
  };

  const handleBulkSubmit = () => {
    bulkForm
      .validateFields()
      .then((values) => {
        console.log('Bulk update values:', values);

        const bulkUpdateData = {
          productIds: selectedProducts,
          discount: values.discount,
          discount_valid_until: values.discount_valid_until ? values.discount_valid_until.format('YYYY-MM-DD') : null
        };

        bulkUpdateDiscountFetch({
          url: URLS.bulkUpdateProductDiscount,
          method: 'PUT',
          params: bulkUpdateData,
          onSuccess: (response) => {
            console.log('Bulk discount update successful:', response);
            // Refresh menu data to get updated values
            // menuData.refresh();
            // Close modal and clear selection
            setBulkModalVisible(false);
            setSelectedProducts([]);
            setSelectAll(false);
            bulkForm.resetFields();
          },
          onError: (error) => {
            console.error('Failed to bulk update discounts:', error);
            // You could show an error notification here
          },
          successMessage: `Discount updated for ${selectedProducts.length} product${selectedProducts.length !== 1 ? 's' : ''}!`,
          showSuccessNotification: true
        });
      })
      .catch((info) => {
        console.log('Validate Failed:', info);
      });
  };

  // Remove discount handlers
  const handleRemoveDiscounts = () => {
    setRemoveModalVisible(true);
  };

  const handleRemoveCancel = () => {
    setRemoveModalVisible(false);
  };

  const handleRemoveConfirm = () => {
    console.log('Removing discounts for products:', selectedProducts);

    removeDiscountFetch({
      url: URLS.removeProductDiscounts,
      method: 'PUT',
      params: {
        productIds: selectedProducts
      },
      onSuccess: (response) => {
        console.log('Discount removal successful:', response);
        // Refresh products data to get updated values
        refetchProducts();
        // Close modal and clear selection
        setRemoveModalVisible(false);
        setSelectedProducts([]);
        setSelectAll(false);
      },
      onError: (error) => {
        console.error('Failed to remove discounts:', error);
        // You could show an error notification here
      },
      successMessage: `Discounts removed from ${selectedProducts.length} product${selectedProducts.length !== 1 ? 's' : ''}!`,
      showSuccessNotification: true
    });
  };

  // Open product in new tab handler
  const handleOpenProduct = (product) => {
    const productUrl = `/product/${product.id}`;
    window.open(productUrl, '_blank');
  };

  const columns = [
    {
      title: (
        <Checkbox
          checked={selectAll}
          indeterminate={selectedProducts.length > 0 && !selectAll}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      dataIndex: 'selection',
      key: 'selection',
      width: 50,
      render: (text, record) => (
        <Checkbox
          checked={selectedProducts.includes(record.id)}
          onChange={(e) => handleProductSelection(record.id, e.target.checked)}
        />
      ),
    },
    {
      title: 'Image',
      dataIndex: 'image',
      key: 'image',
      render: (text, record) => (
        <Image src={record.image_url} fallback={getImageFallbackUrl(record.image_url)} width={50} height={50} style={{ objectFit: 'cover', borderRadius: '100%' }} />
      ),
    },

    {
      title: 'Product Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => {
        const types = parseTypes(record.types);
        const sizes = parseSizes(record.sizes);
        const hasVariations = types.length > 0 || sizes.length > 0;

        if (!hasVariations) {
          return text;
        }

        const variations = [];

        // Add types information
        if (types.length > 0) {
          const typeNames = types.map(type => {
            // Get the first available language name
            const currentLanguage = selectedLanguage || languages?.[0]?.id;
            const typeName = type[`typeName.${currentLanguage}`] ||
              type[`typeName.${languages?.[0]?.id}`] ||
              'Unknown Type';
            return `${typeName}: ${type.price} ${record.currency}`;
          }).join(', ');
          variations.push(`Types: ${typeNames}`);
        }

        // Add sizes information
        if (sizes.length > 0) {
          const sizeNames = sizes.map(size => {
            // Get the first available language name
            const currentLanguage = selectedLanguage || languages?.[0]?.id;
            const sizeName = size[`sizeName.${currentLanguage}`] ||
              size[`sizeName.${languages?.[0]?.id}`] ||
              'Unknown Size';
            return `${sizeName}: ${size.price} ${record.currency}`;
          }).join(', ');
          variations.push(`Sizes: ${sizeNames}`);
        }

        return (
          <div>
            <div style={{ fontWeight: 'bold' }}>{text}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {variations.join(' | ')}
            </div>
          </div>
        );
      },
    },

    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (text, record) => {
        const types = parseTypes(record.types);
        const sizes = parseSizes(record.sizes);
        const hasVariations = types.length > 0 || sizes.length > 0;
        const isEditing = editingProduct === record.id;

        if (!hasVariations) {
          if (isEditing) {
            return (
              <InputNumber
                value={editedValues.price}
                onChange={(value) => handleFieldChange('price', value)}
                style={{ width: '100px' }}
                addonAfter={record.currency}
              />
            );
          }
          return record.price + ' ' + record.currency;
        }

        const prices = [];

        // Add types pricing
        if (types.length > 0) {
          types.forEach((type, index) => {
            const currentLanguage = selectedLanguage || languages?.[0]?.id;
            const typeName = type[`typeName.${currentLanguage}`] ||
              type[`typeName.${languages?.[0]?.id}`] ||
              'Unknown Type';

            if (isEditing) {
              prices.push(
                <div key={`type-${index}`} style={{ marginBottom: '4px' }}>
                  <div style={{ fontSize: '10px', marginBottom: '2px' }}>{typeName}:</div>
                  <InputNumber
                    value={editedValues.types?.[index]?.price}
                    onChange={(value) => handleFieldChange('price', value, index)}
                    style={{ width: '80px' }}
                    size="small"
                    addonAfter={record.currency}
                  />
                </div>
              );
            } else {
              prices.push(`${typeName}: ${type.price} ${record.currency}`);
            }
          });
        }

        // Add sizes pricing
        if (sizes.length > 0) {
          sizes.forEach((size, index) => {
            const currentLanguage = selectedLanguage || languages?.[0]?.id;
            const sizeName = size[`sizeName.${currentLanguage}`] ||
              size[`sizeName.${languages?.[0]?.id}`] ||
              'Unknown Size';

            if (isEditing) {
              prices.push(
                <div key={`size-${index}`} style={{ marginBottom: '4px' }}>
                  <div style={{ fontSize: '10px', marginBottom: '2px' }}>{sizeName}:</div>
                  <InputNumber
                    value={editedValues.sizes?.[index]?.price}
                    onChange={(value) => handleFieldChange('price', value, null, index)}
                    style={{ width: '80px' }}
                    size="small"
                    addonAfter={record.currency}
                  />
                </div>
              );
            } else {
              prices.push(`${sizeName}: ${size.price} ${record.currency}`);
            }
          });
        }

        if (isEditing) {
          return <div style={{ fontSize: '12px' }}>{prices}</div>;
        }

        return (
          <div style={{ fontSize: '12px' }}>
            {prices.map((price, index) => (
              <div key={index}>{price}</div>
            ))}
          </div>
        );
      },
    },

    {
      title: 'Discount',
      dataIndex: 'discount',
      key: 'discount',
      render: (text, record) => {
        const types = parseTypes(record.types);
        const sizes = parseSizes(record.sizes);
        const hasVariations = types.length > 0 || sizes.length > 0;
        const isEditing = editingProduct === record.id;

        if (!hasVariations) {
          if (isEditing) {
            return (
              <InputNumber
                value={editedValues.discount}
                onChange={(value) => handleFieldChange('discount', value)}
                style={{ width: '80px' }}
                min={0}
                max={100}
                addonAfter="%"
              />
            );
          }
          return record.discount > 0 ? `${record.discount}%` : '0%';
        }

        const discounts = [];

        // Add types discounts (types don't have discount field, use main product discount)
        if (types.length > 0) {
          types.forEach((type, index) => {
            const currentLanguage = selectedLanguage || languages?.[0]?.id;
            const typeName = type[`typeName.${currentLanguage}`] ||
              type[`typeName.${languages?.[0]?.id}`] ||
              'Unknown Type';

            if (isEditing) {
              discounts.push(
                <div key={`type-${index}`} style={{ marginBottom: '4px' }}>
                  <div style={{ fontSize: '10px', marginBottom: '2px' }}>{typeName}:</div>
                  <InputNumber
                    value={editedValues.discount}
                    onChange={(value) => handleFieldChange('discount', value)}
                    style={{ width: '70px' }}
                    size="small"
                    min={0}
                    max={100}
                    addonAfter="%"
                  />
                </div>
              );
            } else {
              const discount = record.discount > 0 ? `${record.discount}%` : '0%';
              discounts.push(`${typeName}: ${discount}`);
            }
          });
        }

        // Add sizes discounts (sizes have their own discount field)
        if (sizes.length > 0) {
          sizes.forEach((size, index) => {
            const currentLanguage = selectedLanguage || languages?.[0]?.id;
            const sizeName = size[`sizeName.${currentLanguage}`] ||
              size[`sizeName.${languages?.[0]?.id}`] ||
              'Unknown Size';

            if (isEditing) {
              discounts.push(
                <div key={`size-${index}`} style={{ marginBottom: '4px' }}>
                  <div style={{ fontSize: '10px', marginBottom: '2px' }}>{sizeName}:</div>
                  <InputNumber
                    value={editedValues.sizes?.[index]?.discount}
                    onChange={(value) => handleFieldChange('discount', value, null, index)}
                    style={{ width: '70px' }}
                    size="small"
                    min={0}
                    max={100}
                    addonAfter="%"
                  />
                </div>
              );
            } else {
              const discount = size.discount > 0 ? `${size.discount}%` : '0%';
              discounts.push(`${sizeName}: ${discount}`);
            }
          });
        }

        if (isEditing) {
          return <div style={{ fontSize: '12px' }}>{discounts}</div>;
        }

        return (
          <div style={{ fontSize: '12px' }}>
            {discounts.map((discount, index) => (
              <div key={index}>{discount}</div>
            ))}
          </div>
        );
      },
    },

    {
      title: 'Discount Valid Until',
      dataIndex: 'discount_valid_until',
      key: 'discount_valid_until',
      render: (text, record) => {
        const types = parseTypes(record.types);
        const sizes = parseSizes(record.sizes);
        const hasVariations = types.length > 0 || sizes.length > 0;
        const isEditing = editingProduct === record.id;

        if (!hasVariations) {
          if (isEditing) {
            return (
              <DatePicker
                value={editedValues.discount_valid_until ? dayjs(editedValues.discount_valid_until) : null}
                onChange={(date) => handleFieldChange('discount_valid_until', date ? date.format('YYYY-MM-DD') : null)}
                style={{ width: '120px' }}
                size="small"
                allowClear
              />
            );
          }
          return text || 'No expiry';
        }

        const validityInfo = [];

        // Add types validity (uses main product validity)
        if (types.length > 0) {
          types.forEach((type, index) => {
            const currentLanguage = selectedLanguage || languages?.[0]?.id;
            const typeName = type[`typeName.${currentLanguage}`] ||
              type[`typeName.${languages?.[0]?.id}`] ||
              'Unknown Type';

            if (isEditing) {
              validityInfo.push(
                <div key={`type-${index}`} style={{ marginBottom: '4px' }}>
                  <div style={{ fontSize: '10px', marginBottom: '2px' }}>{typeName}:</div>
                  <DatePicker
                    value={editedValues.discount_valid_until ? dayjs(editedValues.discount_valid_until) : null}
                    onChange={(date) => handleFieldChange('discount_valid_until', date ? date.format('YYYY-MM-DD') : null)}
                    style={{ width: '110px' }}
                    size="small"
                    allowClear
                  />
                </div>
              );
            } else {
              const validity = record.discount_valid_until || 'No expiry';
              validityInfo.push(`${typeName}: ${validity}`);
            }
          });
        }

        // Add sizes validity (currently uses main product validity, could be enhanced to have individual validity)
        if (sizes.length > 0) {
          sizes.forEach((size, index) => {
            const currentLanguage = selectedLanguage || languages?.[0]?.id;
            const sizeName = size[`sizeName.${currentLanguage}`] ||
              size[`sizeName.${languages?.[0]?.id}`] ||
              'Unknown Size';

            if (isEditing) {
              validityInfo.push(
                <div key={`size-${index}`} style={{ marginBottom: '4px' }}>
                  <div style={{ fontSize: '10px', marginBottom: '2px' }}>{sizeName}:</div>
                  <DatePicker
                    value={editedValues.discount_valid_until ? dayjs(editedValues.discount_valid_until) : null}
                    onChange={(date) => handleFieldChange('discount_valid_until', date ? date.format('YYYY-MM-DD') : null)}
                    style={{ width: '110px' }}
                    size="small"
                    allowClear
                  />
                </div>
              );
            } else {
              const validity = record.discount_valid_until || 'No expiry';
              validityInfo.push(`${sizeName}: ${validity}`);
            }
          });
        }

        if (isEditing) {
          return <div style={{ fontSize: '12px' }}>{validityInfo}</div>;
        }

        return (
          <div style={{ fontSize: '12px' }}>
            {validityInfo.map((info, index) => (
              <div key={index}>{info}</div>
            ))}
          </div>
        );
      },
    },

    {
      title: 'Is Expired',
      dataIndex: 'discount_valid_until',
      key: 'discount_valid_until',
      render: (text, record) => {
        const types = parseTypes(record.types);
        const sizes = parseSizes(record.sizes);
        const hasVariations = types.length > 0 || sizes.length > 0;

        if (!hasVariations) {
          // If no discount, show nothing
          if (!record.discount || record.discount <= 0) {
            return null;
          }

          // If discount exists but no validity date, show "On Sale"
          if (!record.discount_valid_until) {
            return <Badge status="success" text="On Sale" />;
          }

          // If both discount and date exist, check if expired
          const isExpired = new Date() > new Date(record.discount_valid_until);
          return isExpired ?
            <Badge status="error" text="Expired" /> :
            <Badge status="success" text="On Sale" />;
        }

        const statusInfo = [];

        // Add types status
        if (types.length > 0) {
          types.forEach(type => {
            const currentLanguage = selectedLanguage || languages?.[0]?.id;
            const typeName = type[`typeName.${currentLanguage}`] ||
              type[`typeName.${languages?.[0]?.id}`] ||
              'Unknown Type';

            // Types use main product discount
            if (!record.discount || record.discount <= 0) {
              return;
            }

            let status;
            if (!record.discount_valid_until) {
              status = <Badge status="success" text="On Sale" />;
            } else {
              const isExpired = new Date() > new Date(record.discount_valid_until);
              status = isExpired ?
                <Badge status="error" text="Expired" /> :
                <Badge status="success" text="On Sale" />;
            }

            statusInfo.push({ name: typeName, status });
          });
        }

        // Add sizes status
        if (sizes.length > 0) {
          sizes.forEach(size => {
            const currentLanguage = selectedLanguage || languages?.[0]?.id;
            const sizeName = size[`sizeName.${currentLanguage}`] ||
              size[`sizeName.${languages?.[0]?.id}`] ||
              'Unknown Size';

            // Sizes have their own discount
            if (!size.discount || size.discount <= 0) {
              return;
            }

            let status;
            if (!record.discount_valid_until) {
              status = <Badge status="success" text="On Sale" />;
            } else {
              const isExpired = new Date() > new Date(record.discount_valid_until);
              status = isExpired ?
                <Badge status="error" text="Expired" /> :
                <Badge status="success" text="On Sale" />;
            }

            statusInfo.push({ name: sizeName, status });
          });
        }

        return (
          <div style={{ fontSize: '12px' }}>
            {statusInfo.map((info, index) => (
              <div key={index} style={{ marginBottom: '4px' }}>
                <div>{info.name}:</div>
                <div>{info.status}</div>
              </div>
            ))}
          </div>
        );
      },
    },

    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (text, record) => {
        const isEditing = editingProduct === record.id;

        if (isEditing) {
          return (
            <Space>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSaveEdit}
                size="small"
              >
                Save
              </Button>
              <Button
                icon={<CloseOutlined />}
                onClick={handleDiscardEdit}
                size="small"
              >
                Discard
              </Button>
            </Space>
          );
        }

        return (
          <Space>
            <Button
              type="primary"
              onClick={() => handleEditProduct(record)}
              size="small"
            >
              Update
            </Button>
            <Button
              icon={<ExportOutlined />}
              onClick={() => handleOpenProduct(record)}
              size="small"
              title="Open product details in new tab"
            >
              Open
            </Button>
          </Space>
        );
      },
    },
  ];


  return (
    <div>
      {/* Search Section */}
      <Card title="Search Products" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={18} md={20}>
            <Input
              placeholder="Search products by name (searches in all languages)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              prefix={<SearchOutlined />}
              allowClear
              onClear={clearSearch}
              style={{ fontSize: '14px' }}
            />
          </Col>
          <Col xs={24} sm={6} md={4}>
            <Button
              onClick={clearAllFiltersAndSearch}
              disabled={totalActiveFilters === 0}
              block
            >
              Clear All
            </Button>
          </Col>
        </Row>
        {debouncedSearchQuery && (
          <div style={{ marginTop: 12, fontSize: '13px', color: '#666' }}>
            <SearchOutlined style={{ marginRight: 6 }} />
            Searching for: "<strong>{debouncedSearchQuery}</strong>"
            {filteredProducts.length > 0 ?
              ` - Found ${filteredProducts.length} product${filteredProducts.length !== 1 ? 's' : ''}` :
              ' - No products found'
            }
          </div>
        )}
      </Card>

      {/* Filters Section */}
      <Card style={{ marginBottom: 16 }}>
        <Collapse
          activeKey={filtersCollapsed ? [] : ['filters']}
          onChange={(keys) => setFiltersCollapsed(keys.length === 0)}
          ghost
        >
          <Collapse.Panel
            key="filters"
            header={
              <Space>
                <FilterOutlined />
                <span>Filters</span>
                {totalActiveFilters > 0 && (
                  <Badge
                    count={totalActiveFilters}
                    style={{ backgroundColor: '#1890ff' }}
                  />
                )}
              </Space>
            }
          >
            <Row gutter={[16, 16]}>
              {/* Category Filters */}
              <Col xs={24} sm={24} md={8}>
                <div>
                  <h4>Categories</h4>
                  <Space direction="vertical">
                    {transformedCategories.map(category => (
                      <Checkbox
                        key={category.id}
                        checked={selectedCategories.includes(category.id)}
                        onChange={(e) => handleCategoryFilter(category.id, e.target.checked)}
                      >
                        {category.translations?.[0]?.name || `Category ${category.id}`}
                      </Checkbox>
                    ))}
                  </Space>
                </div>
              </Col>

              {/* Subcategory Filters */}
              <Col xs={24} sm={24} md={8}>
                <div>
                  <h4>Subcategories</h4>
                  <Space direction="vertical">
                    {transformedSubcategories.map(subcategory => (
                      <Checkbox
                        key={subcategory.id}
                        checked={selectedSubcategories.includes(subcategory.id)}
                        onChange={(e) => handleSubcategoryFilter(subcategory.id, e.target.checked)}
                      >
                        {subcategory.translations?.[0]?.name || `Subcategory ${subcategory.id}`}
                      </Checkbox>
                    ))}
                  </Space>
                </div>
              </Col>

              {/* Brand Filters */}
              <Col xs={24} sm={24} md={8}>
                <div>
                  <h4>Brands</h4>
                  <Space direction="vertical">
                    {transformedBrands.map(brand => (
                      <Checkbox
                        key={brand.id}
                        checked={selectedBrands.includes(brand.id)}
                        onChange={(e) => handleBrandFilter(brand.id, e.target.checked)}
                      >
                        {brand.translations?.[0]?.name || `Brand ${brand.id}`}
                      </Checkbox>
                    ))}
                  </Space>
                </div>
              </Col>
            </Row>

            {/* Clear Filters Button */}
            <div style={{ marginTop: 16 }}>
              <Button
                onClick={clearAllFilters}
                disabled={totalActiveFilters === 0}
              >
                Clear All Filters
              </Button>
              <span style={{ marginLeft: 16 }}>
                Showing {filteredProducts.length} of {productsData?.pagination?.total || 0} products
              </span>
            </div>
          </Collapse.Panel>
        </Collapse>
      </Card>

      {/* Selection Controls */}
      {selectedProducts.length > 0 && (
        <Card style={{ marginBottom: 16 }}>
          <Space>
            <span>
              <strong>{selectedProducts.length}</strong> product{selectedProducts.length !== 1 ? 's' : ''} selected
            </span>
            <Button onClick={clearSelection} size="small">
              Clear Selection
            </Button>
            <Button type="primary" size="small" icon={<EditOutlined />} onClick={handleBulkEdit}>
              Set Bulk Discounts
            </Button>
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={handleRemoveDiscounts}
            >
              Remove Discounts
            </Button>
          </Space>
        </Card>
      )}

      {/* Table */}
      <Table
        dataSource={filteredProducts}
        columns={columns}
        rowKey="id"
        loading={isLoadingProducts}
        pagination={false}
        scroll={{ x: 800 }}
        size="small"
        locale={{
          emptyText: isLoadingProducts ? 'Loading products...' : 'No products found'
        }}
        style={{ marginBottom: '16px' }}
      />

      {/* Products Pagination */}
      {productsData && productsData.pages > 1 && (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <Pagination
            current={currentPage}
            total={productsData.total || 0}
            pageSize={pageSize}
            onChange={(page, size) => {
              setCurrentPage(page);
              if (size !== pageSize) {
                setPageSize(size);
              }
            }}
            showSizeChanger
            showQuickJumper
            pageSizeOptions={['10', '20', '30', '50', '100']}
            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} products`}
          />
        </div>
      )}

      {/* Bulk Discount Modal */}
      <Modal
        title={
          <Space>
            <EditOutlined />
            <span>Set Bulk Discounts</span>
          </Space>
        }
        open={bulkModalVisible}
        onOk={handleBulkSubmit}
        onCancel={handleBulkCancel}
        okText="Apply Discount"
        cancelText="Cancel"
        width={500}
      >
        <div style={{ marginBottom: 16 }}>
          <p style={{ color: '#666', marginBottom: 8 }}>
            You have selected <strong>{selectedProducts.length}</strong> product{selectedProducts.length !== 1 ? 's' : ''}.
          </p>
          <p style={{ color: '#666', fontSize: '14px' }}>
            The selected products will have the same discount applied until the selected duration.
            This will update the discount percentage and validity date for all selected products.
          </p>
        </div>

        <Form
          form={bulkForm}
          layout="vertical"
          initialValues={{
            discount: 0
          }}
        >
          <Form.Item
            name="discount"
            label="Discount Percentage"
            rules={[
              {
                required: true,
                message: 'Please enter a discount percentage'
              },
              {
                type: 'number',
                min: 0,
                max: 100,
                message: 'Discount must be between 0 and 100'
              }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              max={100}
              addonAfter="%"
              placeholder="Enter discount percentage"
            />
          </Form.Item>

          <Form.Item
            name="discount_valid_until"
            label="Discount Valid Until"
            rules={[
              {
                required: false
              }
            ]}
          >
            <DatePicker
              style={{ width: '100%' }}
              placeholder="Select expiry date (optional)"
              allowClear
            />
          </Form.Item>

          <div style={{ backgroundColor: '#f6f8fa', padding: '12px', borderRadius: '6px', fontSize: '13px' }}>
            <strong>Note:</strong> For products with types/sizes variations:
            <ul style={{ marginBottom: 0, marginTop: 8, paddingLeft: 20 }}>
              <li>Types will inherit the main product discount</li>
              <li>Sizes will have their individual discounts updated</li>
            </ul>
          </div>
        </Form>
      </Modal>

      {/* Remove Discounts Confirmation Modal */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            <span>Remove Discounts</span>
          </Space>
        }
        open={removeModalVisible}
        onOk={handleRemoveConfirm}
        onCancel={handleRemoveCancel}
        okText="Remove Discounts"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        width={500}
      >
        <div style={{ marginBottom: 16 }}>
          <p style={{ color: '#666', marginBottom: 12 }}>
            You are about to remove discounts from <strong>{selectedProducts.length}</strong> product{selectedProducts.length !== 1 ? 's' : ''}.
          </p>

          <div style={{ backgroundColor: '#fff7e6', border: '1px solid #ffd591', borderRadius: '6px', padding: '12px', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: '8px', marginTop: '2px' }} />
              <div>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Warning</div>
                <div style={{ fontSize: '14px' }}>
                  This action will:
                  <ul style={{ marginTop: '8px', marginBottom: '0', paddingLeft: '20px' }}>
                    <li>Set discount percentage to 0% for all selected products</li>
                    <li>Clear discount validity dates</li>
                    <li>Reset discounts for all size variations (where applicable)</li>
                    <li>Cannot be undone</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <p style={{ color: '#666', fontSize: '14px' }}>
            Are you sure you want to proceed? This action cannot be undone.
          </p>
        </div>
      </Modal>
    </div>
  );
}

export default Discounts;
